import React, { useEffect, useRef, useCallback, useMemo }from "react";
import { Tooltip, Overlay } from "react-bootstrap";
import Button from 'react-bootstrap/Button';
import { getUiComponentsData } from "services/others";
import useApi from "components/hooks/useApi";
import { ThreeDots } from 'react-loader-spinner';

interface Props {
  info?: string,
  subRoute?: string
}

const ToolTip: React.FC<Props> = ({ info: tooltipInfo, subRoute }) => {
  const [show, setShow] = React.useState(false);
  const [isIframeLoading, setIsIframeLoading] = React.useState(true);
  const [tooltipState, setTooltipState] = React.useState({
    url: "",
    embeddedUrl: "",
    info: ""
  });
  
  const target = useRef(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  
  const {
    action: fetchTooltipData,
    data: tooltipData,
    loading: tooltipLoading,
    error: tooltipError
  } = useApi(getUiComponentsData);

  const getDynamicRoute = useMemo(() => {
    try {
      const pathname = window?.location?.pathname;
      const routeBreakdowns = pathname?.split("/").pop();
      return routeBreakdowns || 'default';
    } catch (error) {
      return 'default';
    }
  }, []);

  useEffect(() => {
    if (!tooltipData?.success) {
      return;
    }

    const { url = "", embeddedUrl = "", description = "" } = tooltipData.data.properties;
    
    setTooltipState({
      url,
      embeddedUrl,
      info: description
    });
  }, [tooltipData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (show && 
          tooltipRef.current && 
          !tooltipRef.current.contains(event.target as Node) &&
          target.current && 
          !target.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [show]);

  const handleClose = useCallback(() => {
    setShow(false);
    setIsIframeLoading(true);
    setTooltipState({
      url: "",
      embeddedUrl: "",
      info: ""
    });
  }, []);
  
  const triggerHandler = useCallback(() => {
    if (!show) {
      fetchTooltipData({
        type: "TOOL_TIP",
        route: getDynamicRoute,
        subRoute: subRoute || ''
      });
    }
    setShow(!show);
  }, [show, fetchTooltipData, getDynamicRoute, subRoute]);
  
  const handleUrlClick = useCallback(() => {
    if (tooltipState.url) {
      window.open(tooltipState.url);
    }
  }, [tooltipState.url]);

  const onIframeLoad = useCallback(() => {
    setIsIframeLoading(false);
  }, []);

  return (
    <>
      <img
       src="/images/tooltip.svg"
        // width={width}
        // height={height}
        ref={target}
        onClick={triggerHandler}
      />
      
      <Overlay target={target.current} show={show} placement="bottom">
        {(props) => (
          <Tooltip id="overlay-example" className="theme-tooltip" {...props}>
            <div ref={tooltipRef}>
              <div className="tooltip-header">      
                <button className="tooltip-close" onClick={handleClose}>×</button>
              </div>

              {tooltipLoading ? (
                <div className="d-flex justify-content-center p-3">
                  <ThreeDots color="#ffffff" height={50} width={50} />
                </div>
              ) : (
                <>
                  {tooltipState.embeddedUrl && (
                    <div className="position-relative">
                      {isIframeLoading && (
                        <div className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center">
                          <ThreeDots color="#ffffff" height={50} width={50} />
                        </div>
                      )}
                      <iframe 
                        src={tooltipState.embeddedUrl} 
                        width="100%" 
                        height="350" 
                        frameBorder="0"
                        onLoad={onIframeLoad}
                        style={{opacity: isIframeLoading ? 0 : 1}}
                      />
                    </div>
                  )}
                  <div>{tooltipState.info}</div>
              
                  {tooltipState.url && (
                    <Button className="tooltip-button" variant="light" onClick={handleUrlClick}>
                      View Full Scribe <img src="/images/ArrowSquareUpRight.svg" alt="arrow" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </Tooltip>
        )}
      </Overlay>
    </>
  );
};

export default React.memo(ToolTip);
